/* eslint-disable react-hooks/exhaustive-deps */

// React
import { Fragment, useEffect, useState } from "react"

// Components
import { Card, FooterMenu, Pagination } from "components"
import { DropdownCard } from "../DropdownCard"
import { FilterByDate } from "../FilterByDate"
import { FooterSummary } from "../FooterSummary"
import { TableRegister } from "../TableRegister"

// Contexts
import { RowIndexContext } from "../../contexts"

// Third-Party Libraries
import moment from "moment"

// Types
import type { SuccessFetch } from "types"
import type { GroupName } from "pages/Finance/Transaction/types"
import type { PaginationType, RegisterDateType } from "../../types"

// Utils
import { useApi } from "utils"
import { useFilter } from "pages/Finance/Journal/utils/hooks"
import {
  exportPdf,
  prevYear,
  thisMonth,
  thisWeek,
  thisYear,
  today
} from "utils/functions"
import { ExportPdf } from "../ExportPdf"
import { ExportExcel } from "../ExportExcel"
import { usePagination } from "../../utils/hooks"
import { PERMISION } from "security"

type Response = {
  payload: {
    beginingbalance: number
    currentbalance: number
    data_register: RegisterDateType[]
    pagination: PaginationType
  }
}

export function RegisterCard() {
  const api = useApi()
  const todayDate = today()
  const prevYearDate = prevYear()
  const thisYearDate = thisYear()
  const thisMonthDate = thisMonth()
  const thisWeekDate = thisWeek()
  const [data, setData] = useState<RegisterDateType[]>([])
  const [isLoading, setLoading] = useState(false)

  const {
    currentPage,
    limit,
    size,
    totalPage,
    total,
    getRowNumber,
    onChangeLimit,
    onChangePage,
    onChangePaginationData,
    resetPage
  } = usePagination()

  const { filter, handleChangeFilter } = useFilter({
    GroupCardID: "" as GroupName,
    TraderID: "",
    dateFrom: thisMonthDate.fromDate,
    dateTo: todayDate
  })

  const fetch = () =>
    api
      .postForm(`/regbydatarange/getdatabycard?page=${currentPage}&limit=${limit}`, {
        GroupCardID: filter.GroupCardID,
        TraderID: filter.TraderID,
        StartDate: filter.dateFrom,
        EndDate: filter.dateTo
      })
      .then((res: SuccessFetch<Response>) => {
        const data = res.data.payload

        onChangePaginationData(data.pagination)

        setData(
          data.data_register?.map((item) => ({
            ...item,
            checked: false
          })) ?? []
        )
      }).catch((error) => {
        const message = error.response?.data?.message || "Invalid data"
        console.log(message);
      }).finally(() => {
        setLoading(false)
      })

  useEffect(() => {

    resetPage()

    if (filter.GroupCardID && filter.TraderID) {
      fetch()
    }
  }, [filter, limit])

  useEffect(() => {
    fetch()
  }, [currentPage])

  const handleCheckData = (index: number) => {
    const newData = [...data]
    data[index].checked = !data[index].checked

    setData(newData)
  }


  return (
    <Fragment>
      <section className="mt-2">
        <Card >
          <Card.Body>
            <section className="container flex-col lg:flex lg:flex-row justify-center lg:justify-between">
              <div className="w-full lg:w-1/2 flex-col lg:flex lg:flex-row justify-center lg:justify-start items-center">
                <div className="label-text pb-1 font-bold pe-6">GROUP CARD</div>
                <DropdownCard
                  value={null}
                  groupCardId={filter.GroupCardID as GroupName}
                  onChangeGroup={(item) =>
                    handleChangeFilter({
                      GroupCardID: item?.value as GroupName
                    })
                  }
                  onChange={(item) =>
                    handleChangeFilter({ TraderID: item?.value })
                  }
                />
              </div>
            </section>
            <FilterByDate
              permission={PERMISION.FINANCE.REGISTER.BY_CARD.PERMISION}
              dateFrom={filter.dateFrom}
              dateTo={filter.dateTo}
              onChangeDateFrom={(dateFrom) => {
                const isValid = moment(dateFrom).isBefore(filter.dateTo)
                handleChangeFilter({
                  dateFrom,
                  dateTo: isValid ? filter.dateTo : dateFrom
                })
              }}
              onChangeDateTo={(dateTo) => handleChangeFilter({ dateTo })}
              onClickPrevYear={() =>
                handleChangeFilter({
                  dateFrom: prevYearDate.fromDate,
                  dateTo: prevYearDate.toDate
                })
              }
              onClickThisYear={() =>
                handleChangeFilter({
                  dateFrom: thisYearDate.fromDate,
                  dateTo: thisYearDate.toDate
                })
              }
              onClickThisWeek={() =>
                handleChangeFilter({
                  dateFrom: thisWeekDate.fromDate,
                  dateTo: thisWeekDate.toDate
                })
              }
              onClickThisMonth={() =>
                handleChangeFilter({
                  dateFrom: thisMonthDate.fromDate,
                  dateTo: thisMonthDate.toDate
                })
              }
              onClickToday={() =>
                handleChangeFilter({
                  dateFrom: todayDate,
                  dateTo: todayDate
                })
              }
            />
          </Card.Body>
        </Card>
      </section>

      <div className="overflow-x-scroll max-h-[calc(100vh-500px)]">
        <RowIndexContext.Provider value={getRowNumber}>
          <TableRegister
            data={data}
            filter={filter}
            isLoading={isLoading}
            handleCheckData={handleCheckData}
            fetchList={fetch}
            permission={PERMISION.FINANCE.REGISTER.BY_CARD.PERMISION}
          />
          {Boolean(!isLoading && data.length) && (
            <Pagination
              currentPage={currentPage}
              totalPage={totalPage}
              size={size}
              limit={limit}
              total={total}
              onPageChange={onChangePage}
              onAddShow={(res) => onChangeLimit(Number(res))}
            />

          )}
        </RowIndexContext.Provider>
        <section className="mt-2">
          <FooterMenu
            customRightElement={
              <FooterSummary data={data} dateFrom={filter.dateFrom} />
            }
            export={{
              actiontype: "export",
              onClick: () => {
                ExportExcel({
                  data: data,
                  filter: {
                    dateFrom: filter.dateFrom,
                    dateTo: filter.dateTo
                  },
                  fileName: "finance_register_by_card",
                  title: "FINANCE REGISTER BY CARD"
                })
              }
            }}
            pdf={{
              actiontype: "pdf",
              onClick: () => {
                exportPdf({
                  documentComponent: (
                    <ExportPdf
                      data={data}
                      filter={{
                        dateFrom: filter.dateFrom,
                        dateTo: filter.dateTo
                      }}
                      title="FINANCE REGISTER BY CARD"
                    />
                  ),
                  fileName: "finance_register_by_card"
                })
              }
            }}
          />
        </section>
      </div>
    </Fragment>
  )
}
